/**
 * 操作日志查询 API（operation-log-query，ADM-31）。
 *
 * - GET /operation-logs -> 分页列表（9 维筛选 + 数据范围，operated_at 倒序）
 * - GET /operation-logs/:id -> 详情（前后值/业务原因/detail 结构化明细）
 */
import { ApiPaths } from './api.config';
import { http } from './transport';

/** 分页结果信封（与各 *.api.ts 本地 Paginated 同构）。 */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** 操作日志列表行（后端列表项字段，含 COALESCE 兜底展示名）。 */
export interface OperationLogListItem {
  id: string;
  operatedAt: string;
  operatorId: string;
  operatorName: string | null;
  role: string | null;
  campusId: string | null;
  campusName: string | null;
  bizType: string;
  bizId: string;
  action: string;
  reason: string | null;
  ipDeviceSummary: string | null;
  result: 'SUCCESS' | 'FAILED';
}

/** 操作日志详情（含前后值与 detail 结构化明细）。 */
export interface OperationLogDetail extends OperationLogListItem {
  beforeValue: string | null;
  afterValue: string | null;
  detail: Record<string, unknown> | null;
}

/** 操作日志列表查询参数（9 维筛选 + 分页）。 */
export interface OperationLogQuery {
  operatorKeyword?: string;
  role?: string;
  campusId?: string;
  bizType?: string;
  action?: string;
  bizId?: string;
  operatedFrom?: string;
  operatedTo?: string;
  result?: 'SUCCESS' | 'FAILED';
  page?: number;
  pageSize?: number;
}

/** 业务类型中文标签（下拉选项与列表展示）。 */
export const BIZ_TYPE_LABELS: Record<string, string> = {
  'student-mobile-plaintext-view': '学生手机号明文查看',
  'billing-order': '订单',
  'billing-payment': '支付',
  'wechat-payment': '微信支付',
  'billing-reconciliation': '支付对账',
  'billing-receivable-plan': '应收计划',
  deduction: '消课扣减',
  'staff-wechat-bind': '员工微信绑定',
  'system-config': '系统设置',
  'operations-export': '导出下载',
};

/** 敏感业务类型（前端派生「敏感」标签，对齐 UI 方案风险级别展示口径）。 */
export const SENSITIVE_BIZ_TYPES = new Set([
  'student-mobile-plaintext-view',
  'billing-reconciliation',
  'deduction',
]);

function listOperationLogs(
  query: OperationLogQuery = {},
): Promise<Paginated<OperationLogListItem>> {
  return http.get<Paginated<OperationLogListItem>>(
    ApiPaths.operationLogs.list,
    query as unknown as Record<string, unknown>,
  );
}

function getOperationLogDetail(id: string): Promise<OperationLogDetail> {
  return http.get<OperationLogDetail>(ApiPaths.operationLogs.detail(id));
}

/** 操作日志 API 唯一对外入口。 */
export const operationLogsApi = {
  listOperationLogs,
  getOperationLogDetail,
};
